import { type ZodType, z } from "zod";
import type { FormElement, FormElementList } from "../../types/form";
import { isStatic } from "../is-static";

export const generateZodSchema = (formElements: FormElementList | undefined): z.ZodObject<Record<string, ZodType>> => {
	const schemaObject: Record<string, z.ZodType> = {};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: easy to read
	const addType = (element: FormElement): void => {
		if (isStatic(element.fieldType)) return;

		let elementSchema: z.ZodType;

		const isRequired =
			"required" in element && element.required === true && !("disabled" in element && element.disabled === true);

		switch (element.fieldType) {
			case "Input":
				if (element.type === "email") {
					elementSchema = z.email();
					break;
				}
				if (element.type === "url") {
					elementSchema = z.url();
					break;
				}
				if (element.type === "number") {
					if (isRequired) {
						elementSchema = z.preprocess((val) => String(val), z.string().min(1)).pipe(z.coerce.number());
					} else {
						elementSchema = z.coerce.number();
					}

					break;
				}

				if (isRequired) {
					elementSchema = z.string().min(1);
				} else {
					elementSchema = z.string();
				}

				break;
			case "Checkbox":
				if (isRequired) {
					elementSchema = z.coerce.boolean().refine((value) => value === true);
				} else {
					elementSchema = z.coerce.boolean();
				}
				break;
			case "Select":
				if (isRequired) {
					elementSchema = z.string().min(1);
				} else {
					elementSchema = z.string();
				}
				break;
			case "ToggleGroup":
				elementSchema =
					element.type === "single" ? z.string().min(1, "Please an item") : z.tuple([z.string()], z.string());
				break;
			case "MultiSelect":
				if (isRequired) {
					elementSchema = z.preprocess((val) => (val === "" ? [] : val), z.array(z.string()).min(1));
				} else {
					elementSchema = z.preprocess((val) => (val === "" ? [] : val), z.array(z.string()));
				}
				break;
			case "RadioGroup":
				if (isRequired) {
					elementSchema = z.string().min(1);
				} else {
					elementSchema = z.string();
				}
				break;
			default:
				if (isRequired) {
					elementSchema = z.string().min(1);
				} else {
					elementSchema = z.string();
				}
		}

		// Ensure fieldSchema is of type ZodType
		schemaObject[element.name] = elementSchema;
	};

	formElements?.flat().forEach(addType);

	return z.object(schemaObject);
};
